import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';

interface NotificationContextProps {
  isMuted: boolean;
  toggleMute: () => void;
}

const NotificationContext = createContext<NotificationContextProps | undefined>(undefined);

export const useNotification = (): NotificationContextProps => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  // Get initial mute state from localStorage if available
  const [isMuted, setIsMuted] = useState<boolean>(() => {
    const savedMuteState = localStorage.getItem('notificationMuted');
    return savedMuteState ? JSON.parse(savedMuteState) : false;
  });

  // Toggle mute state
  const toggleMute = () => {
    setIsMuted(prevState => !prevState);
  };

  // Save mute state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('notificationMuted', JSON.stringify(isMuted));
  }, [isMuted]);

  const value = {
    isMuted,
    toggleMute
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;
